import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import {
  Text,
  init,
  save,
  load,
  change,
  applyChanges,
  clone,
  getAllChanges,
} from 'automerge';
import Button from '../components/Button';
import { SectionItem } from '../components/Chrome';
import {
  Drawer,
  DrawerExit,
  DrawerLink,
  DrawerList,
  DrawerTrigger,
} from '../components/Drawer';
import Loader from '../components/Loader';
import {
  Modal,
  ModalEditableBody,
  ModalEditableName,
  ModalExit,
  ModalPart,
} from '../components/Modal';
import { useToast } from '../components/Toast';
import { SPACE, SPACE_TAGS, VIEWER } from '../utilities/queries';
import { UPDATE_TOPIC, UPDATE_COMMENT_BODY } from '../utilities/mutations';
import { useSpaceContext } from '../contexts/space';
import { useSyncContext } from '../contexts/sync';
import ConnectedClients from './shared/ConnectedClients';

const EditTopicModal = ({
  topicId,
  topicName,
  commentId,
  commentBody,
  topicTags,
  spaceSlug,
  onClose,
}) => {
  const toast = useToast();
  const params = useParams();

  const { spaceVars } = useSpaceContext();
  const { loading: loadingSpace, data: dataSpace } = useQuery(SPACE, {
    variables: { slug: params.s, ...spaceVars },
  });
  const { loading: loadingSpaceTags, data: dataSpaceTags } = useQuery(
    SPACE_TAGS,
    {
      variables: { slug: spaceSlug },
    }
  );
  const { loading: loadingViewer, data: dataViewer } = useQuery(VIEWER);
  const [updateTopic] = useMutation(UPDATE_TOPIC, {
    variables: { id: topicId },
  });
  const [updateCommentBody] = useMutation(UPDATE_COMMENT_BODY, {
    variables: { id: commentId },
  });
  const [isUnsaved, setIsUnsaved] = useState(false);
  const [shake, setShake] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const ws = useSyncContext();

  const isCollaborationEnabled =
    import.meta.env.VITE_COLLABORATION_ENABLED === 'true';

  const leaveComment = () => {
    if (ws && dataViewer) {
      ws.emit('COMMENT_LEAVE', {
        id: commentId,
      });
    }
  };

  const closeModal = () => {
    if (!shake && !submitting) {
      if (isUnsaved) {
        setShake(true);
        toast({ children: `Don't forget to save!` });
        setTimeout(() => setShake(false), 500);
      } else {
        leaveComment();
        if (onClose) {
          onClose();
        }
      }
    }
  };

  const [shadow, setShadow] = useState();
  const [clients, setClients] = useState();

  const nameRef = useRef();
  const bodyRef = useRef();
  const [nameCursor, setNameCursor] = useState(0);
  const [bodyCursor, setBodyCursor] = useState(0);
  const nameCursorRef = useRef(nameCursor);
  const bodyCursorRef = useRef(bodyCursor);

  const modalRef = useRef();

  const close = () => {
    leaveComment();

    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (
      ws &&
      dataViewer &&
      dataSpace?.space?.earlyAccess &&
      isCollaborationEnabled
    ) {
      ws.emit('COMMENT', {
        userId: dataViewer.viewer.id,
        commentId,
      });

      ws.on('COLLABORATION_SHADOW', (data) => setShadow(data));
      ws.on('COLLABORATION_CLIENTS', (data) => setClients(data));
    }
  }, [ws, dataViewer, dataSpace?.space?.earlyAccess]);

  // Create current document
  const currentDoc = change(init(), (doc) => {
    doc.name = new Text(topicName);
    doc.body = new Text(commentBody);
  });

  // Sends updated tags to server
  const emitTags = (updatedTags) => {
    if (ws && dataSpace?.space?.earlyAccess && isCollaborationEnabled) {
      ws.emit('COLLABORATION_TAGS', {
        id: commentId,
        updatedTags,
      });
    }
  };

  // Sends updated comment to server
  const emitComment = ({ name, body, cursor }) => {
    if (ws && dataSpace?.space?.earlyAccess && isCollaborationEnabled) {
      const updatedDoc = change(currentDoc, (doc) => {
        if (name) {
          doc.name = name;

          // Save old name cursor position
          setNameCursor(cursor);
        }

        if (body) {
          doc.body = body;

          // Save old body cursor position
          setBodyCursor(cursor);
        }
      });

      ws.emit('COLLABORATION_NAME_BODY', {
        id: commentId,
        cursor,
        updatedDoc: save(updatedDoc),
      });
    }
  };

  return (
    <Modal ref={modalRef} shake={shake} onClose={() => closeModal()}>
      {loadingSpace ||
      loadingSpaceTags ||
      loadingViewer ||
      (dataSpace?.space?.earlyAccess && !shadow && isCollaborationEnabled) ? (
        <ModalPart>
          <Loader />
        </ModalPart>
      ) : (
        <>
          <ModalExit onClick={() => close()} dontCloseOnClick />
          {dataSpace?.space?.earlyAccess && isCollaborationEnabled && (
            <>
              {!clients ? (
                <SectionItem style={{ padding: '10px 16px' }}>
                  <Loader />
                </SectionItem>
              ) : (
                <>
                  <ConnectedClients clients={clients} />
                </>
              )}
            </>
          )}
          <Formik
            initialValues={{
              name: (isCollaborationEnabled && shadow?.name) || topicName,
              tags: (isCollaborationEnabled && shadow?.tags) || [
                ...topicTags.map((tag) => tag.id),
              ],
              body: (isCollaborationEnabled && shadow?.body) || commentBody,
            }}
            validate={(values) => {
              if (values.name !== topicName || values.body !== commentBody) {
                setIsUnsaved(true);
              } else {
                setIsUnsaved(false);
              }
            }}
            onSubmit={async (values) => {
              try {
                setSubmitting(true);

                if (
                  JSON.stringify(values.tags) !== JSON.stringify(topicTags) ||
                  values.name !== topicName
                ) {
                  await updateTopic({
                    variables: {
                      name: values.name,
                      tags: values.tags.map((tag) => {
                        return tag;
                      }),
                    },
                  });
                }

                if (values.body !== commentBody) {
                  await updateCommentBody({
                    variables: {
                      body: values.body,
                    },
                  });
                }

                close();
                setSubmitting(false);
              } catch (error) {
                toast({ children: error.message });
                setSubmitting(false);

                return;
              }
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => {
              useLayoutEffect(() => {
                nameCursorRef.current = nameCursor;
                bodyCursorRef.current = bodyCursor;
              }, [nameCursor, bodyCursor]);

              // Apply changes received from other clients

              useEffect(() => {
                if (ws && isCollaborationEnabled) {
                  ws.on('COLLABORATION_TAGS_UPDATE', (updatedTags) => {
                    if (values.tags !== updatedTags) {
                      setFieldValue('tags', updatedTags);
                    }
                  });

                  setTimeout(() => {
                    ws.on(
                      'COLLABORATION_NAME_BODY_UPDATE',
                      ({ cursor, updatedDoc }) => {
                        const changes = getAllChanges(
                          load(new Uint8Array(updatedDoc))
                        );

                        const [finalDoc] = applyChanges(
                          clone(currentDoc),
                          changes
                        );

                        const isNewName = finalDoc.name !== values.name;
                        const isNewBody = finalDoc.body !== values.body;

                        // Move saved name cursor by 1 to account for new character added
                        if (isNewName && nameCursorRef.current >= cursor) {
                          setNameCursor(nameCursorRef.current + 1);
                        }

                        // Move saved body cursor by 1 to account for new character added
                        if (isNewBody && bodyCursorRef.current >= cursor) {
                          setBodyCursor(bodyCursorRef.current + 1);
                        }

                        if (isNewName) {
                          setFieldValue('name', finalDoc.name.toString());

                          // Restore name cursor position
                          setTimeout(() => {
                            nameRef.current.setSelectionRange(
                              nameCursorRef.current,
                              nameCursorRef.current
                            );
                          }, 1);
                        }

                        if (isNewBody) {
                          setFieldValue('body', finalDoc.body.toString());

                          // Restore body cursor position
                          setTimeout(() => {
                            bodyRef.current.setSelectionRange(
                              bodyCursorRef.current,
                              bodyCursorRef.current
                            );
                          }, 1);
                        }
                      }
                    );
                  }, 1);
                }
              }, [ws]);

              return (
                <form onSubmit={handleSubmit}>
                  <ModalEditableName
                    ref={nameRef}
                    type="text"
                    name="name"
                    disabled={isSubmitting}
                    onChange={(e) => {
                      emitComment({
                        name: e.target.value,
                        cursor: e.target.selectionStart,
                      });
                      handleChange(e);
                    }}
                    onClick={(e) =>
                      isCollaborationEnabled &&
                      setNameCursor(e.target.selectionStart)
                    }
                    onBlur={handleBlur}
                    value={values.name}
                  />
                  <ModalEditableBody
                    ref={bodyRef}
                    type="text"
                    name="body"
                    disabled={isSubmitting}
                    onChange={(e) => {
                      emitComment({
                        body: e.target.value,
                        cursor: e.target.selectionStart,
                      });
                      handleChange(e);
                    }}
                    onClick={(e) =>
                      isCollaborationEnabled &&
                      setBodyCursor(e.target.selectionStart)
                    }
                    onBlur={handleBlur}
                    value={values.body}
                    autoFocus
                    modalRef={modalRef}
                  />
                  <ModalPart isFooter>
                    {dataSpaceTags?.space?.tags.length !== 0 && (
                      <Drawer>
                        <DrawerTrigger>
                          <Button type="button" disabled={isSubmitting}>
                            {values.tags.length !== 0
                              ? `${values.tags.length} ${
                                  values.tags.length === 1 ? 'tag' : 'tags'
                                }`
                              : 'Tags'}
                          </Button>
                        </DrawerTrigger>
                        <DrawerList>
                          <DrawerExit />
                          {dataSpaceTags.space.tags.map((tag) => (
                            <DrawerLink
                              as="button"
                              isActive={values.tags.includes(tag.id)}
                              onClick={() => {
                                if (values.tags.includes(tag.id)) {
                                  const updatedTags = values.tags.filter(
                                    (innerTagId) => innerTagId !== tag.id
                                  );

                                  emitTags(updatedTags);
                                  setFieldValue('tags', updatedTags);
                                } else {
                                  const updatedTags = [...values.tags, tag.id];

                                  emitTags(updatedTags);
                                  setFieldValue('tags', updatedTags);
                                }
                              }}
                              dontCloseOnClick
                            >
                              {tag.name}
                            </DrawerLink>
                          ))}
                        </DrawerList>
                      </Drawer>
                    )}
                    <Button
                      type="submit"
                      disabled={
                        (values.body.toString().trim() === commentBody &&
                          values.name.toString().trim() === topicName &&
                          JSON.stringify(values.tags) ===
                            JSON.stringify(topicTags.map((tag) => tag.id))) ||
                        isSubmitting
                      }
                      isPrimary
                    >
                      Save
                    </Button>
                  </ModalPart>
                </form>
              );
            }}
          </Formik>
        </>
      )}
    </Modal>
  );
};

export default EditTopicModal;
